import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { M365User, M365UsersResponse, getLicenseDisplayName } from '../../types/m365Users'
import { M365UserDetailModal } from './M365UserDetailModal'
import { ResetPasswordModal } from './ResetPasswordModal'
import { AssignLicenseModal } from './AssignLicenseModal'
import { getCustomerGraphClientId } from '../../config/authConfig'

interface M365UsersTabProps {
  customerId: string
  partnerCenterId: string | undefined
}

// Filter types
type StatusFilter = 'all' | 'enabled' | 'disabled'
type LicenseFilter = 'all' | 'licensed' | 'unlicensed'
type ActivityFilter = 'all' | 'active' | 'inactive' | 'never'

export function M365UsersTab({ customerId, partnerCenterId }: M365UsersTabProps) {
  const { getAccessToken, getCustomerGraphAccessToken } = useAuth()
  const [users, setUsers] = useState<M365User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [authenticating, setAuthenticating] = useState(false)

  // License mapping (skuId -> display name)
  const [licenseMap, setLicenseMap] = useState<Record<string, string>>({})

  // Filter states
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [licenseFilter, setLicenseFilter] = useState<LicenseFilter>('all')
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all')

  // Store the customer tenant ID once we get it
  const customerTenantIdRef = useRef<string | null>(null)
  // Store the customer tenant token
  const customerTenantTokenRef = useRef<string | null>(null)
  // Prevent concurrent auth attempts (causes interaction_in_progress error)
  const authInProgressRef = useRef<Promise<string> | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  // Modal states
  const [selectedUser, setSelectedUser] = useState<M365User | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showAssignLicenseModal, setShowAssignLicenseModal] = useState(false)

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [needsAuthorization, setNeedsAuthorization] = useState(false)
  const [authorizing, setAuthorizing] = useState(false)

  // GDAP status
  const [gdapStatus, setGdapStatus] = useState<'active' | 'none' | 'pending' | 'expired' | 'error' | 'checking' | null>(null)
  const [gdapMessage, setGdapMessage] = useState<string | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Track if we've already tried interactive auth for this session
  const triedInteractiveRef = useRef(false)

  // Get the customer tenant token (cached or fresh)
  // Strategy: Check GDAP status first, then try silent, if it fails try interactive popup automatically
  // Uses mutex to prevent concurrent auth attempts (causes interaction_in_progress error)
  const getCustomerToken = useCallback(async (): Promise<string> => {
    // If we already have a token, return it immediately
    if (customerTenantTokenRef.current) {
      return customerTenantTokenRef.current
    }

    // If auth is already in progress, wait for it
    if (authInProgressRef.current) {
      return authInProgressRef.current
    }

    // Start new auth flow
    const authPromise = (async () => {
      const partnerToken = await getAccessToken()

      // Step 1: Get the customer tenant ID and GDAP status using partner token
      if (!customerTenantIdRef.current) {
        setGdapStatus('checking')
        const tenantInfo = await api.getM365TenantId(partnerToken, customerId) as {
          tenantId: string
          gdapStatus: 'active' | 'none' | 'pending' | 'expired' | 'error'
          hasActiveGdap: boolean
          gdapMessage?: string
        }
        customerTenantIdRef.current = tenantInfo.tenantId
        setGdapStatus(tenantInfo.gdapStatus)
        setGdapMessage(tenantInfo.gdapMessage || null)

        // If GDAP is not active, don't attempt authentication (avoid popup error)
        if (!tenantInfo.hasActiveGdap && tenantInfo.gdapStatus !== 'error') {
          throw new Error(`GDAP_NOT_CONFIGURED: ${tenantInfo.gdapMessage || 'No active GDAP relationship with this customer'}`)
        }
      }

      // Step 2: Get the customer tenant token (user authentication via GDAP)
      if (customerTenantIdRef.current) {
        setAuthenticating(true)
        try {
          // Always try with silentOnly: false to allow popup if silent fails
          // The useAuth hook will try silent first, then popup if needed
          customerTenantTokenRef.current = await getCustomerGraphAccessToken(
            customerTenantIdRef.current,
            { silentOnly: false }
          )
          triedInteractiveRef.current = true
        } finally {
          setAuthenticating(false)
        }
      }

      if (!customerTenantTokenRef.current) {
        throw new Error('Failed to authenticate to customer tenant')
      }

      return customerTenantTokenRef.current
    })()

    // Store the promise so concurrent calls wait for it
    authInProgressRef.current = authPromise

    try {
      return await authPromise
    } finally {
      // Clear the promise when done (success or failure)
      authInProgressRef.current = null
    }
  }, [customerId, getAccessToken, getCustomerGraphAccessToken])

  // Fetch available licenses to build skuId -> name mapping
  // Calls Microsoft Graph directly using the customer tenant token
  const fetchLicenseMapping = useCallback(async (token: string) => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/subscribedSkus', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Graph API error: ${response.status}`)
      }
      const data = await response.json()
      const mapping: Record<string, string> = {}
      if (data.value) {
        for (const sku of data.value) {
          // Use skuPartNumber to get friendly name from our mapping
          const displayName = getLicenseDisplayName(sku.skuPartNumber) || sku.skuPartNumber
          mapping[sku.skuId] = displayName
        }
      }
      setLicenseMap(mapping)
    } catch (err) {
      console.warn('Failed to fetch license mapping:', err)
      // Non-fatal - we'll fall back to showing skuId
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    if (!partnerCenterId) {
      setError('Customer is not linked to Partner Center')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerToken()

      // Fetch users and license mapping in parallel
      const [usersResponse] = await Promise.all([
        api.getM365Users(token, customerId, {
          search: debouncedSearch || undefined,
        }) as Promise<M365UsersResponse>,
        fetchLicenseMapping(token),
      ])
      setUsers(usersResponse.users)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users'
      // Check the full error object for AADSTS90099 (may be in nested properties)
      const fullErrorString = JSON.stringify(err)
      const hasAuthError = errorMessage.includes('AADSTS90099') ||
        errorMessage.includes('has not been authorized') ||
        errorMessage.includes('NEEDS_AUTHORIZATION') ||
        fullErrorString.includes('AADSTS90099')

      // Handle GDAP not configured (detected before auth popup)
      if (errorMessage.includes('GDAP_NOT_CONFIGURED')) {
        // GDAP status is already set by getCustomerToken, just show the error
        const message = errorMessage.replace('GDAP_NOT_CONFIGURED: ', '')
        setError(message)
      } else if (hasAuthError || errorMessage.includes('user_cancelled')) {
        // User cancelled likely means the consent popup showed an error
        setNeedsAuthorization(true)
        setError('The Partner License Portal app needs to be authorized for this customer tenant. Click "Grant Consent" to enable access.')
      } else if (errorMessage.includes('GDAP access') || errorMessage.includes('AADSTS50020')) {
        setError('You do not have GDAP access to this customer. Contact your admin to be added to AdminAgents or HelpdeskAgents security group.')
      } else if (errorMessage.includes('Access denied') || errorMessage.includes('403')) {
        setError('No delegated admin access to this customer tenant. Please verify GDAP is configured.')
      } else if (errorMessage.includes('Admin consent')) {
        setError('Admin consent is required for this customer tenant. Please contact your admin.')
      } else if (errorMessage.includes('interaction_required') || errorMessage.includes('popup')) {
        setError('Authentication cancelled or blocked. Please allow popups and try again.')
      } else {
        setError(errorMessage)
      }
      // Clear the token so user can try again
      customerTenantTokenRef.current = null
    } finally {
      setLoading(false)
    }
  }, [customerId, partnerCenterId, debouncedSearch, getCustomerToken, fetchLicenseMapping])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Filter users based on selected filters
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Status filter
      if (statusFilter === 'enabled' && !user.accountEnabled) return false
      if (statusFilter === 'disabled' && user.accountEnabled) return false

      // License filter
      const hasLicense = user.assignedLicenses && user.assignedLicenses.length > 0
      if (licenseFilter === 'licensed' && !hasLicense) return false
      if (licenseFilter === 'unlicensed' && hasLicense) return false

      // Activity filter (based on lastSignInDateTime)
      if (activityFilter !== 'all') {
        const lastSignIn = user.lastSignInDateTime ? new Date(user.lastSignInDateTime) : null
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

        if (activityFilter === 'never') {
          // Show only users who have never signed in
          if (lastSignIn !== null) return false
        } else if (activityFilter === 'active') {
          // Show only users who signed in within the last 30 days
          if (!lastSignIn || lastSignIn < thirtyDaysAgo) return false
        } else if (activityFilter === 'inactive') {
          // Show only users who have signed in, but more than 30 days ago
          if (!lastSignIn || lastSignIn >= thirtyDaysAgo) return false
        }
      }

      return true
    })
  }, [users, statusFilter, licenseFilter, activityFilter])

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize)
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredUsers.slice(startIndex, startIndex + pageSize)
  }, [filteredUsers, currentPage, pageSize])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, licenseFilter, activityFilter, debouncedSearch])

  // Build the admin consent URL for the customer tenant
  // Uses the Customer Graph app (separate from Partner Center app) to avoid
  // consent issues with the Partner Center API scope
  const getAdminConsentUrl = () => {
    if (!customerTenantIdRef.current) return null
    // Use the Customer Graph app ID - this app only has Microsoft Graph permissions
    const clientId = getCustomerGraphClientId()
    const redirectUri = encodeURIComponent(window.location.origin + '/auth-callback')
    // Use the /adminconsent endpoint for proper admin consent flow
    return `https://login.microsoftonline.com/${customerTenantIdRef.current}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`
  }

  // Open admin consent in a new tab (popups often get blocked)
  const handleAuthorize = async () => {
    if (!customerTenantIdRef.current) {
      // Need to get the tenant ID first
      try {
        const partnerToken = await getAccessToken()
        const tenantInfo = await api.getM365TenantId(partnerToken, customerId)
        customerTenantIdRef.current = tenantInfo.tenantId
      } catch (err) {
        setError('Failed to get customer tenant ID')
        return
      }
    }

    const consentUrl = getAdminConsentUrl()
    if (consentUrl) {
      // Open in new tab - user will consent there, then come back and click Retry
      window.open(consentUrl, '_blank')
      setError(null)
      setActionSuccess('Consent page opened in a new tab. After granting consent, close that tab and click "Retry" below.')
    }
  }

  // Retry authentication after consent has been granted
  const handleRetryAuth = async () => {
    try {
      setAuthorizing(true)
      setError(null)
      setNeedsAuthorization(false)
      customerTenantTokenRef.current = null // Clear old token to force re-auth
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to authenticate')
    } finally {
      setAuthorizing(false)
    }
  }

  const handleToggleStatus = async (user: M365User) => {
    try {
      setActionLoading(user.id)
      const token = await getCustomerToken()
      await api.setM365UserStatus(token, customerId, user.id, !user.accountEnabled)

      // Update local state
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, accountEnabled: !u.accountEnabled } : u
      ))

      setActionSuccess(`User ${user.accountEnabled ? 'disabled' : 'enabled'} successfully`)
      setTimeout(() => setActionSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResetPassword = (user: M365User) => {
    setSelectedUser(user)
    setShowResetPasswordModal(true)
  }

  const handleEditUser = (user: M365User) => {
    setSelectedUser(user)
    setShowDetailModal(true)
  }

  const handleAssignLicense = (user: M365User) => {
    setSelectedUser(user)
    setShowAssignLicenseModal(true)
  }

  const handleUserUpdated = (updatedUser: M365User) => {
    setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u))
    setShowDetailModal(false)
    setActionSuccess('User updated successfully')
    setTimeout(() => setActionSuccess(null), 3000)
  }

  const handleLicenseUpdated = () => {
    fetchUsers() // Refresh to get updated license info
    setShowAssignLicenseModal(false)
    setActionSuccess('Licenses updated successfully')
    setTimeout(() => setActionSuccess(null), 3000)
  }

  if (!partnerCenterId) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <svg className="mx-auto h-12 w-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-yellow-800">Not Connected to Partner Center</h3>
        <p className="mt-1 text-sm text-yellow-600">
          This customer needs to be linked to Partner Center to manage M365 users.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Success message */}
      {actionSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <svg className="h-5 w-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm text-green-700">{actionSuccess}</span>
        </div>
      )}

      {/* GDAP not configured message */}
      {(gdapStatus === 'none' || gdapStatus === 'pending' || gdapStatus === 'expired') && error && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                {gdapStatus === 'none' && 'GDAP Not Configured'}
                {gdapStatus === 'pending' && 'GDAP Pending Approval'}
                {gdapStatus === 'expired' && 'GDAP Relationship Expired'}
              </h3>
              <div className="mt-2 text-sm text-orange-700">
                <p>{gdapMessage || error}</p>
              </div>
              <div className="mt-4">
                <div className="flex space-x-3">
                  <a
                    href="https://partner.microsoft.com/dashboard/v2/customers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open Partner Center
                  </a>
                  <button
                    onClick={() => {
                      setGdapStatus(null)
                      setGdapMessage(null)
                      setError(null)
                      customerTenantIdRef.current = null
                      fetchUsers()
                    }}
                    className="inline-flex items-center px-4 py-2 border border-orange-600 text-orange-700 text-sm font-medium rounded-md hover:bg-orange-50"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Check Again
                  </button>
                </div>
                <p className="mt-3 text-xs text-orange-600">
                  {gdapStatus === 'none' && 'To manage M365 users, you need to create a GDAP relationship with this customer in Partner Center.'}
                  {gdapStatus === 'pending' && 'The customer needs to approve the GDAP request before you can manage their M365 users.'}
                  {gdapStatus === 'expired' && 'Create a new GDAP relationship with this customer in Partner Center to continue managing their M365 users.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error message (for other errors) */}
      {error && gdapStatus !== 'none' && gdapStatus !== 'pending' && gdapStatus !== 'expired' && (
        <div className={`${needsAuthorization ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className={`h-5 w-5 ${needsAuthorization ? 'text-yellow-400' : 'text-red-400'} mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-sm ${needsAuthorization ? 'text-yellow-700' : 'text-red-700'}`}>{error}</span>
            </div>
            <button onClick={() => { setError(null); setNeedsAuthorization(false); }} className={`${needsAuthorization ? 'text-yellow-400 hover:text-yellow-600' : 'text-red-400 hover:text-red-600'}`}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {needsAuthorization && (
            <div className="mt-3">
              <p className="text-sm text-yellow-700 mb-2">
                To manage M365 users, you need to grant consent for this app in the customer tenant.
                {' '}You must have <strong>Global Administrator</strong> GDAP role for this customer.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleRetryAuth}
                  disabled={authorizing}
                  className="inline-flex items-center px-4 py-2 border border-yellow-600 text-yellow-700 text-sm font-medium rounded-md hover:bg-yellow-50 disabled:opacity-50"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry
                </button>
                <button
                  onClick={handleAuthorize}
                  disabled={authorizing}
                  className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  {authorizing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Opening...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Grant Consent
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
        {/* Status filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium border ${
                statusFilter === 'all'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-l-md`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('enabled')}
              className={`px-3 py-1.5 text-xs font-medium border-t border-b ${
                statusFilter === 'enabled'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Enabled
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('disabled')}
              className={`px-3 py-1.5 text-xs font-medium border ${
                statusFilter === 'disabled'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-r-md`}
            >
              Disabled
            </button>
          </div>
        </div>

        {/* License filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">License:</span>
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setLicenseFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium border ${
                licenseFilter === 'all'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-l-md`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setLicenseFilter('licensed')}
              className={`px-3 py-1.5 text-xs font-medium border-t border-b ${
                licenseFilter === 'licensed'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Licensed
            </button>
            <button
              type="button"
              onClick={() => setLicenseFilter('unlicensed')}
              className={`px-3 py-1.5 text-xs font-medium border ${
                licenseFilter === 'unlicensed'
                  ? 'bg-yellow-600 text-white border-yellow-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } rounded-r-md`}
            >
              Unlicensed
            </button>
          </div>
        </div>

        {/* Activity filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Activity:</span>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
            className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All</option>
            <option value="active">Active (30 days)</option>
            <option value="inactive">Inactive (30+ days)</option>
            <option value="never">Never signed in</option>
          </select>
        </div>

        {/* Filter count */}
        <div className="ml-auto text-sm text-gray-500">
          {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          <svg className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Users table */}
      {loading || authenticating || gdapStatus === 'checking' ? (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-3 text-gray-600">
            {gdapStatus === 'checking' ? 'Checking GDAP status...' : authenticating ? 'Authenticating to customer tenant...' : 'Loading users...'}
          </span>
        </div>
      ) : paginatedUsers.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {users.length > 0 ? 'No users match the current filters.' : searchTerm ? 'Try a different search term.' : 'No M365 users in this tenant.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Licenses
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                          {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      </div>
                      <div className="ml-4">
                        <Link
                          to={`/customers/${customerId}/users/${user.id}`}
                          className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
                        >
                          {user.displayName}
                        </Link>
                        <div className="text-sm text-gray-500">{user.userPrincipalName}</div>
                        {user.mail && user.mail !== user.userPrincipalName && (
                          <div className="text-xs text-gray-400">{user.mail}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.jobTitle || '-'}</div>
                    <div className="text-sm text-gray-500">{user.department || ''}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.assignedLicenses && user.assignedLicenses.length > 0 ? (
                        user.assignedLicenses.slice(0, 3).map((license, idx) => {
                          // Use license map from tenant, fall back to skuPartNumber lookup or skuId
                          const displayName = licenseMap[license.skuId] || license.skuId
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              title={displayName}
                            >
                              {displayName.length > 25 ? displayName.substring(0, 22) + '...' : displayName}
                            </span>
                          )
                        })
                      ) : (
                        <span className="text-sm text-gray-400">No licenses</span>
                      )}
                      {user.assignedLicenses && user.assignedLicenses.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{user.assignedLicenses.length - 3} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(user)}
                      disabled={actionLoading === user.id}
                      className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium ${
                        user.accountEnabled
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors disabled:opacity-50`}
                    >
                      {actionLoading === user.id ? (
                        <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                      ) : (
                        <span className={`h-2 w-2 rounded-full mr-1.5 ${user.accountEnabled ? 'bg-green-400' : 'bg-red-400'}`}></span>
                      )}
                      {user.accountEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit user"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleResetPassword(user)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Reset password"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleAssignLicense(user)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Manage licenses"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredUsers.length)} of {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                {filteredUsers.length !== users.length && ` (${users.length} total)`}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showDetailModal && selectedUser && (
        <M365UserDetailModal
          user={selectedUser}
          customerId={customerId}
          customerTenantId={customerTenantIdRef.current || ''}
          onClose={() => setShowDetailModal(false)}
          onUserUpdated={handleUserUpdated}
        />
      )}

      {showResetPasswordModal && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          customerId={customerId}
          customerTenantId={customerTenantIdRef.current || ''}
          onClose={() => setShowResetPasswordModal(false)}
        />
      )}

      {showAssignLicenseModal && selectedUser && (
        <AssignLicenseModal
          user={selectedUser}
          customerId={customerId}
          customerTenantId={customerTenantIdRef.current || ''}
          onClose={() => setShowAssignLicenseModal(false)}
          onLicenseUpdated={handleLicenseUpdated}
        />
      )}
    </div>
  )
}
